import { useEffect, useState, useRef, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import { ChatMessage } from '../types'

interface UseSocketOptions {
  url: string
  autoConnect?: boolean
  onBroadcastStarted?: (data: BroadcastStartedData) => void
  onBroadcastEnded?: (data: BroadcastEndedData) => void
  onBroadcastUpdated?: (data: BroadcastUpdatedData) => void
}

interface BroadcastStartedData {
  broadcastId: number
  broadcasterName: string
  showName: string | null
  showDescription: string | null
  startedAt: string
}

interface BroadcastEndedData {
  broadcastId: number
  endedAt: string
}

interface BroadcastUpdatedData {
  broadcastId: number
  currentTrackName: string | null
  currentTrackArtist: string | null
}

export function useSocket({
  url,
  autoConnect = true,
  onBroadcastStarted,
  onBroadcastEnded,
  onBroadcastUpdated,
}: UseSocketOptions) {
  const [isConnected, setIsConnected] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!autoConnect) return

    const socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    })

    socketRef.current = socket

    // Connection events
    socket.on('connect', () => {
      console.log('Socket.io connected')
      setIsConnected(true)
      setReconnectAttempt(0)

      // Emit listener:join when connected
      socket.emit('listener:join')
    })

    socket.on('disconnect', (reason) => {
      console.log('Socket.io disconnected:', reason)
      setIsConnected(false)
    })

    socket.on('reconnect_attempt', (attempt) => {
      console.log('Reconnection attempt:', attempt)
      setReconnectAttempt(attempt)
    })

    socket.on('reconnect', (attempt) => {
      console.log('Reconnected after', attempt, 'attempts')
      setReconnectAttempt(0)
    })

    socket.on('connect_error', (error) => {
      console.error('Socket.io connection error:', error)
    })

    // Chat events
    socket.on('chat:message', (message: ChatMessage) => {
      setMessages((prev) => [...prev, message])
    })

    socket.on('chat:history', (history: ChatMessage[]) => {
      setMessages(history)
    })

    socket.on('chat:cleared', () => {
      console.log('🗑️ Chat cleared by broadcaster')
      setMessages([])
    })

    // Broadcast events
    socket.on('broadcast:started', (data: BroadcastStartedData) => {
      console.log('Broadcast started:', data)
      if (onBroadcastStarted) {
        onBroadcastStarted(data)
      }
    })

    socket.on('broadcast:ended', (data: BroadcastEndedData) => {
      console.log('Broadcast ended:', data)
      if (onBroadcastEnded) {
        onBroadcastEnded(data)
      }
    })

    socket.on('broadcast:updated', (data: BroadcastUpdatedData) => {
      console.log('Broadcast updated:', data)
      if (onBroadcastUpdated) {
        onBroadcastUpdated(data)
      }
    })

    return () => {
      socket.emit('listener:leave')
      socket.disconnect()
    }
  }, [url, autoConnect, onBroadcastStarted, onBroadcastEnded, onBroadcastUpdated])

  const sendMessage = useCallback((senderName: string, message: string) => {
    if (socketRef.current && isConnected) {
      const messageData = {
        nickname: senderName,
        message: message,
        timestamp: new Date().toISOString(),
        role: 'listener' as const,
      }
      socketRef.current.emit('chat:send', messageData)
    }
  }, [isConnected])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  return {
    isConnected,
    messages,
    sendMessage,
    clearMessages,
    reconnectAttempt,
  }
}
